import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import Coin from "../components/Coin";
import ChunkyDatePicker from "../components/ChunkyDatePicker";
import { useStore, STARTING_COINS } from "../context/StoreContext";
import sfx from "../utils/sound";
import usePageMeta from "../hooks/usePageMeta";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const Welcome = () => {
  const [value, setValue] = useState("");
  const [dob, setDobLocal] = useState("");
  const { name, setName, setDob } = useStore();
  const nav = useNavigate();
  usePageMeta(
    "Welcome to 67Cafe — Play, Shop & Win",
    "Enter your name to start playing games, unlocking stickers, and shopping with pretend coins at 67Cafe."
  );

  const submit = (e) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    setName(trimmed);
    if (dob) setDob(dob);
    sfx.sixSeven();
    nav("/shop");
  };

  const googleSignIn = () => {
    const redirect = window.location.origin + "/auth/callback";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirect);
  };

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div
      className="min-h-screen w-full dots-bg flex flex-col items-center justify-center px-4 py-10"
      data-testid="welcome-page"
    >
      <div
        className="chunky-card pop-in w-full max-w-lg p-6 sm:p-10 text-center"
        style={{ background: "#fff" }}
      >
        <div className="text-6xl sm:text-7xl mb-2 bob" aria-hidden="true">
          ☕
        </div>
        <h1
          className="font-display text-3xl sm:text-5xl leading-tight"
          style={{ color: "#FF5A5F" }}
        >
          Welcome to 67Cafe!
        </h1>
        <p className="mt-3 text-base sm:text-lg" style={{ color: "#484848" }}>
          Play, shop &amp; win — a silly pretend hangout for everyone.
          <br />
          Who&#39;s hanging out today, <strong>{name || "friend"}</strong>?
        </p>

        <form onSubmit={submit} className="mt-6 flex flex-col gap-4">
          <input
            data-testid="welcome-name-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Type your name (e.g. Zoe)"
            autoFocus
            maxLength={20}
            className="w-full rounded-2xl px-5 py-4 font-display text-2xl outline-none text-center"
            style={{
              border: "3px solid #2b2b2b",
              boxShadow: "0 5px 0 #2b2b2b",
              background: "#FFF9E6",
              color: "#484848",
            }}
          />
          <div className="text-left">
            <label
              className="block text-xs font-display mb-1"
              style={{ color: "#484848" }}
            >
              Birthday (optional — all ages welcome)
            </label>
            <ChunkyDatePicker
              value={dob}
              onChange={setDobLocal}
              testIdPrefix="welcome-dob"
              maxYear={new Date().getFullYear()}
            />
            {/* Keep the legacy testid alive for any pre-existing e2e checks. */}
            <input
              type="hidden"
              data-testid="welcome-dob-input"
              value={dob}
              readOnly
            />
          </div>
          <button
            type="submit"
            disabled={!value.trim()}
            data-testid="welcome-start-btn"
            className="btn-3d w-full"
            style={{ fontSize: 22 }}
          >
            Let&#39;s Play!
            <ArrowRight size={22} strokeWidth={3} />
          </button>
        </form>

        <div
          className="my-5 flex items-center gap-3 text-xs"
          style={{ color: "#8a8a8a" }}
          aria-hidden="true"
        >
          <span style={{ flex: 1, height: 2, background: "#2b2b2b22" }} />
          or
          <span style={{ flex: 1, height: 2, background: "#2b2b2b22" }} />
        </div>

        <button
          type="button"
          onClick={googleSignIn}
          data-testid="welcome-google-btn"
          className="btn-3d w-full btn-secondary"
          style={{ fontSize: 17 }}
        >
          <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Sign in with Google (optional)
        </button>

        <div
          className="mt-6 flex items-center justify-center gap-2 text-sm"
          style={{ color: "#484848" }}
          data-testid="welcome-coin-info"
        >
          <Sparkles size={14} strokeWidth={3} />
          You&#39;ll start with
          <span className="coin-pill">
            <Coin size={20} />
            {STARTING_COINS} coins
          </span>
          to spend!
        </div>
      </div>

      <footer
        data-testid="welcome-footer"
        className="mt-6 w-full max-w-lg text-center text-xs"
        style={{ color: "#484848" }}
      >
        <p className="mb-2 flex items-center justify-center gap-2 flex-wrap">
          <span aria-hidden="true">☕</span>
          <span>67Cafe — a pretend hangout. Coins are imaginary. Coffee is not.</span>
        </p>
        <nav className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          <Link to="/privacy" data-testid="welcome-footer-privacy" className="underline" style={{ color: "#484848" }}>
            Privacy
          </Link>
          <Link to="/terms" data-testid="welcome-footer-terms" className="underline" style={{ color: "#484848" }}>
            Terms
          </Link>
          <Link to="/dmca" data-testid="welcome-footer-dmca" className="underline" style={{ color: "#484848" }}>
            DMCA
          </Link>
          <Link
            to="/legal-notices"
            data-testid="welcome-footer-legal"
            className="underline"
            style={{ color: "#484848" }}
          >
            Legal Notices
          </Link>
          <a
            href="mailto:mohammed@alghafly.com"
            data-testid="welcome-footer-contact"
            className="underline"
            style={{ color: "#484848" }}
          >
            Contact
          </a>
        </nav>
      </footer>
    </div>
  );
};

export default Welcome;
